import {Alert} from 'src/design/atoms/Alert';
import {Text} from 'src/design/atoms/Text';
import {useDisclosure} from '@mantine/hooks';
import {IconInfoCircle} from '@tabler/icons-react';
import React, {useState} from 'react';
import type {SuperAdminResponse} from 'src/api/ProactApiClient';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {SidebarLinkSuperAdmins} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useGetSuperAdminsQuery} from 'src/redux/queries/userInstance';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {ChangePrimaryInstanceModal} from './components/ChangePrimaryInstanceModal';
import {DemoteSuperAdminModal} from './components/DemoteSuperAdminModal';
import {PromoteSuperAdminModal} from './components/PromoteSuperAdminModal';
import {useSuperAdminColumns} from './hooks/useSuperAdminColumns';

export const SuperAdminListPage: React.FC = () => {
  useSetDocumentTitle('Super Admins');

  const [promoteOpened, {open: openPromote, close: closePromote}] =
    useDisclosure(false);
  const [demoteTarget, setDemoteTarget] =
    useState<SuperAdminResponse | null>(null);
  const [changePrimaryTarget, setChangePrimaryTarget] =
    useState<SuperAdminResponse | null>(null);

  const {data: superAdmins = [], isLoading, isError, isFetching, refetch} =
    useGetSuperAdminsQuery(undefined, {refetchOnMountOrArgChange: true});

  const columns = useSuperAdminColumns();

  const tableState = useTableState({
    identifier: 'super-admin-list-page',
    columns,
    defaultColumnVisibility: {},
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 20,
    },
    defaultSorting: [{id: 'email', desc: false}],
    defaultShowColumnFilters: false,
  });

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: false,
      manualFiltering: false,
      manualPagination: false,
      columns,
      data: superAdmins,
      getRowId: row => String(row.accountId),
      renderRowActions: ({row}) => (
        <TableRowActions>
          <TableRowActions.Menu width={200}>
            <TableRowActions.MenuItem
              onClick={() => setChangePrimaryTarget(row.original)}>
              Change primary instance
            </TableRowActions.MenuItem>
            <TableRowActions.MenuItem
              color="red"
              onClick={() => setDemoteTarget(row.original)}>
              Remove super admin
            </TableRowActions.MenuItem>
          </TableRowActions.Menu>
        </TableRowActions>
      ),
      renderTopToolbarCustomActions: () => (
        <TableToolbar>
          <TableToolbar.Actions>
            <TableToolbar.Action variant="create" onClick={openPromote}>
              Promote
            </TableToolbar.Action>
          </TableToolbar.Actions>
        </TableToolbar>
      ),
    },
    totalCount: superAdmins.length,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return (
    <ContentWrapper>
      <PageHeader>
        <PageHeader.Title
          icon={SidebarLinkSuperAdmins.icon}
          subtitle="Manage platform super administrators">
          Super Admins
        </PageHeader.Title>
      </PageHeader>

      {!isLoading && superAdmins.length === 0 && (
        <Alert
          variant="light"
          color="blue"
          icon={<IconInfoCircle />}
          title="No super admins"
          mb="md">
          <Text size="sm">
            Use the Promote button to grant super admin access to a user by
            email.
          </Text>
        </Alert>
      )}

      {table}

      <PromoteSuperAdminModal
        opened={promoteOpened}
        onClose={closePromote}
      />

      <DemoteSuperAdminModal
        target={demoteTarget}
        onClose={() => setDemoteTarget(null)}
      />

      <ChangePrimaryInstanceModal
        target={changePrimaryTarget}
        onClose={() => setChangePrimaryTarget(null)}
      />
    </ContentWrapper>
  );
};

export default SuperAdminListPage;

routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.SuperAdmins(),
  element: <SuperAdminListPage />,
  isPrivate: true,
  requireSuperAdmin: true,
});
